import TEDatePicker, { TEDatePickerRef } from "@/components/common/TEDatePicker"
import Text from "@/components/common/Typography/Text"
import { Card, Checkbox, Col, Row } from "antd"
import SelectDateFrom from "./SelectDateFrom"
import SelectDateTo from "./SelectDateTo"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { flightActions } from "@/lib/redux/features/flight/reduce"

import { getMinFlights } from "@/utils/flight/getMinFlights"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { refSelectPeople } from "./SelectPeople.wrapper"
import { useTranslation } from "react-i18next"

type SelectDateFromToProps = {
  indexInputFlight: any,
  size?: "small" | 'default',
  layout?: 'default' | 'hero',
  /** Hero date bar without header/checkbox — for results form matching airport fields */
  compact?: boolean,
  onlyContent?: boolean
}

export type SelectDateFromToRef = {
  focuse: (ctx?: 'from' | 'to') => void
}

const SelectDateFromTo = forwardRef<SelectDateFromToRef, SelectDateFromToProps>(({ size = 'default', layout = 'default', compact = false, onlyContent, indexInputFlight }, ref) => {
  const checkedItinerary = useAppSelector(state => state.flight.checkedItinerary)
  const isMultiCities = useAppSelector(state => state.flight.isMultiCities)
  const isHero = layout === 'hero'
  const dateFrom = useAppSelector(state => state.flight.inputFlights[indexInputFlight]?.dateFrom)
  const dateTo = useAppSelector(state => state.flight.inputFlights[indexInputFlight]?.dateTo)
  const dispatch = useAppDispatch()
  const refTEDatePicker = useRef<TEDatePickerRef>(null)
  const [selector, setSelector] = useState<'from' | 'to' | undefined>()
  // Select stable store slice; derive calendar labels in useMemo (selector must not
  // allocate a new {} on every getSnapshot — that hangs SSR via useSyncExternalStore).
  const minFlightsByMonths = useAppSelector(state => state.flight.minFlightsByMonths)
  const searchMin = useMemo(() => {
    return Object.values(minFlightsByMonths)
      .map((obj) => {
        const values = Object.values(obj)
        return values.reduce((obj1, fare) => {
          return {
            ...obj1,
            [fare.date]: `${Math.floor(fare.lowestFare / 1000)}k`,
          }
        }, {} as Record<string, string>)
      })
      .reduce((obj, minFares) => ({ ...obj, ...minFares }), {} as Record<string, string>)
  }, [minFlightsByMonths])
  const { getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  // Keep return date after departure — also when dateTo is missing (one-way URL has no dt1;
  // getValidDayjs(undefined) shows "today" and looks like Ngày đi > Ngày về)
  useEffect(() => {
    if (isMultiCities || !dateFrom) return
    const fromDayjs = getValidDayjs(dateFrom)
    if (!fromDayjs.isValid()) return

    const toDayjs = dateTo ? getValidDayjs(dateTo) : null
    const needsFix = !toDayjs
      || !toDayjs.isValid()
      || fromDayjs.isAfter(toDayjs, "day")
      || fromDayjs.isSame(toDayjs, "day")

    if (needsFix) {
      dispatch(flightActions.changeDateTo({
        indexInputFlight,
        dateTo: fromDayjs.add(1, "day").format("YYYY-MM-DD"),
      }))
    }
  }, [dateFrom, dateTo, dispatch, getValidDayjs, indexInputFlight, isMultiCities])

  useImperativeHandle(ref, () => {
    return {
      focuse(ctx) {
        if (ctx === 'to') {
          setSelector('to')
        } else {
          setSelector('from')
        }
        refTEDatePicker.current?.open()
      },
    }
  }, [refTEDatePicker.current])

  return (
    <TEDatePicker
      ref={refTEDatePicker}
      value={[getValidDayjs(dateFrom), getValidDayjs(dateTo)]}
      onChange={([from, to]) => {
        dispatch(flightActions.changeDateFrom({ indexInputFlight, dateFrom: from }))
        if (to) {
          // Validate: if dateFrom > dateTo, adjust dateTo to dateFrom + 1 day
          const fromDayjs = getValidDayjs(from)
          const toDayjs = getValidDayjs(to)
          const validTo = fromDayjs.isAfter(toDayjs) || fromDayjs.isSame(toDayjs, 'day')
            ? fromDayjs.add(1, 'day').format('YYYY-MM-DD')
            : to
          dispatch(flightActions.changeDateTo({ indexInputFlight, dateTo: validTo }))
          dispatch(flightActions.changeCheckedItinerary(true))
        } else if (from && dateTo) {
          // When only start date changed (selector='from'), validate against existing dateTo
          const fromDayjs = getValidDayjs(from)
          const toDayjs = getValidDayjs(dateTo)
          if (fromDayjs.isAfter(toDayjs) || fromDayjs.isSame(toDayjs, 'day')) {
            dispatch(flightActions.changeDateTo({ indexInputFlight, dateTo: fromDayjs.add(1, 'day').format('YYYY-MM-DD') }))
          }
        }
      }}
      onClose={() => refSelectPeople.current?.open()}
      range={!isMultiCities && (checkedItinerary || selector === 'to')}
      variant="flight"
      popupProps={{
        placement: 'bottomRight'
      }}
      selector={selector}
      subData={searchMin}
      onCalendarChange={(dateStr) => {
        getMinFlights(dateStr)
      }}
    >
      {isHero ? (
        <div className={`home__searching--hero-dates-block${compact ? " home__searching--hero-dates-block--compact" : ""}`}>
          {compact && !isMultiCities ? (
            <div
              className="home__searching--hero-dates-header home__searching--hero-dates-header--compact"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span aria-hidden />
              <Checkbox
                classNames={{ root: "home__searching--checkbox home__searching--hero-dates-roundtrip" }}
                checked={!!checkedItinerary}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  dispatch(flightActions.changeCheckedItinerary(e.target.checked))
                }
              >
                {t("flight.round_trip")}
              </Checkbox>
            </div>
          ) : !compact ? (
            <div className="home__searching--hero-dates-header">
              <Text className="home__searching--hero-dates-heading">
                {t("flight.departure_date")}
              </Text>
              <Checkbox
                classNames={{ root: "home__searching--checkbox home__searching--hero-dates-roundtrip" }}
                checked={checkedItinerary}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  dispatch(flightActions.changeCheckedItinerary(e.target.checked))
                }
              >
                {t("flight.round_trip")}
              </Checkbox>
            </div>
          ) : null}
          <div
            className={`home__searching--hero-dates-bar${checkedItinerary ? " home__searching--hero-dates-bar--round-trip" : ""}${compact ? " home__searching--hero-dates-bar--compact" : ""}${isMultiCities ? " home__searching--hero-dates-bar--one-way" : ""}`}
          >
            <div
              className="home__searching--hero-dates-segment home__searching--hero-dates-segment--from"
              onClick={() => {
                setSelector("from")
                refTEDatePicker.current?.open()
              }}
              role="presentation"
            >
              <SelectDateFrom
                onlyContent
                size={size}
                layout="hero"
                heroSegment
                compact={compact}
                hideItineraryToggle
                indexInputFlight={indexInputFlight}
              />
            </div>
            {!isMultiCities && (
              <>
                <div className="home__searching--hero-dates-divider" aria-hidden />
                <div
                  className={`home__searching--hero-dates-segment home__searching--hero-dates-segment--to${checkedItinerary ? " home__searching--hero-dates-segment--active" : " home__searching--hero-dates-segment--disabled"}`}
                  onClick={() => {
                    if (!checkedItinerary) {
                      dispatch(flightActions.changeCheckedItinerary(true))
                    }
                    setSelector("to")
                    refTEDatePicker.current?.open()
                  }}
                  role="presentation"
                >
                  <SelectDateTo
                    onlyContent
                    size={size}
                    layout="hero"
                    heroSegment
                    compact={compact}
                    indexInputFlight={indexInputFlight}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <Row>
          <Col span={24}>
            <Card>
              <Row
                gutter={[15, 15]}
                onClick={() => refTEDatePicker.current?.open()}
              >
                <Col
                  onClick={() => {
                    setSelector('from')
                  }}
                  flex={isMultiCities ? 1 : undefined}
                  span={isMultiCities ? undefined : 12}
                >
                  <SelectDateFrom onlyContent size={size} indexInputFlight={indexInputFlight} />
                </Col>
                {!isMultiCities && (
                  <Col span={12}
                    onClick={() => {
                      if (!checkedItinerary) {
                        dispatch(flightActions.changeCheckedItinerary(true))
                      }
                      setSelector('to')
                    }}
                  >
                    <SelectDateTo
                      size={size}
                      onlyContent
                      onChangeReturn={(isReture) => {
                        setSelector(isReture ? 'to' : 'from')
                      }}
                      indexInputFlight={indexInputFlight}
                    />
                  </Col>
                )}
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </TEDatePicker>
  )
})

SelectDateFromTo.displayName = 'SelectDateFromTo'

export default SelectDateFromTo